/* eslint-disable no-restricted-syntax */
/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-nested-ternary */
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Location } from '@components/Hotels/types';
import InfiniteScroll from 'react-infinite-scroll-component';
import ButtonPrimary from '@components/common/ButtonPrimary';
import { store } from '@context/store';
import actionTypes from '@context/actionTypes';
import ResortPassAnimation from '@components/common/ResortPassAnimation/ResortPassAnimation';
import HotelApi from '@customTypes/hotel-api';
import { getDateWithDashes, pastEscapedSearchDate } from '@helpers/date';
import { useRouter } from 'next/router';
import { HotelApiResponse, HotelApiResponseItem } from '@customTypes/hotel-api-response';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import useSessionStorage from '@hooks/useSessionStorage';
import { search } from '@events/globals';
import { filterZeroResultsViewed } from '@events/filters';
import LOCATION_TYPE from '@constants/locationTypes';
import { getHotelsV5 } from '@utils/services';
import ResultList from './ResultList';

const RESULTS_PER_PAGE = 30;
let appliedFilters = '';
type Props = {
  data: any;
  setData: Dispatch<SetStateAction<any>>;
  selectedLocation: Location | null;
  searchWithoutQuery: boolean;
  setTitleHotelName: Dispatch<SetStateAction<string>> | undefined;
  filterParams: string[][] | undefined;
  clearFilters: () => void;
  resetData: () => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function Locations({
  data,
  setData,
  resetData,
  selectedLocation,
  searchWithoutQuery,
  setTitleHotelName,
  filterParams,
  clearFilters,
  isLoading,
  setIsLoading,
}: Props) {
  const [currentLocation, setCurrentLocation] = useState<Location>();
  const [isResetEnabled, setIsResetEnabled] = useState<boolean>(false);
  const [numHotels, setNumHotels] = useState(0);
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { searchDate, onlyShowAvailableHotels, srpScrollPosition, windowUrl, srpData } = state;
  const router = useRouter();
  const { getItem } = useSessionStorage();
  const productTypeIds = router.query.pid as string;
  const newAndNotable = router.query.shownew as string;
  const noDateSearch = router.query.nodate !== undefined;

  const [loadMoreClicked, setLoadMoreClicked] = useState(false);

  useEffect(() => {
    if (noDateSearch && searchDate) {
      // remove the searchDate
      dispatch({
        type: actionTypes.SET_SEARCH_DATE,
        payload: undefined,
      });

      // delete the "nodate" queryString so we don't get stuck
      const url = new URL(window.location.href);
      url.searchParams.delete('nodate');
      router.replace(`${url.pathname}?${url.searchParams.toString()}`, undefined, {
        shallow: true,
      });
    }
  }, [noDateSearch, searchDate, dispatch, router]);

  const getMoreResults = useCallback(
    (stageValue?: number, totalValue?: number, extraHotelsValue?: HotelApi[]) => {
      if (
        selectedLocation &&
        (selectedLocation.type === LOCATION_TYPE.CITY ||
          selectedLocation.type === LOCATION_TYPE.CITY_ALIAS ||
          selectedLocation.type === LOCATION_TYPE.ALIAS)
      ) {
        let { extraHotels, stage, total } = data.previousInfo ?? {
          extraHotels: null,
          stage: null,
          page: null,
        };
        if (stageValue && totalValue && extraHotelsValue) {
          stage = stageValue;
          total = totalValue;
          extraHotels = extraHotelsValue;
        }
        if (extraHotels && extraHotels.length > 0) {
          const pageHotels = extraHotels.slice(0, RESULTS_PER_PAGE);
          const remainingHotels = extraHotels.slice(RESULTS_PER_PAGE);
          setData({
            ...data,
            hotels: [...data.hotels, ...pageHotels],
            previousInfo: { ...data.previousInfo, extraHotels: remainingHotels },
            stageData: {
              ...data.stageData,
              currentStage: data.stageData.currentStage + 1,
              page: 0,
            },
            forceReload: false,
          });
        } else if (
          stage &&
          stage === data.stageData.currentStage &&
          Math.ceil(total / RESULTS_PER_PAGE) > data.stageData.page
        ) {
          setData({
            ...data,
            stageData: { ...data.stageData, page: data.stageData.page + 1 },
            forceReload: false,
          });
        } else if (stage && stage > data.stageData.currentStage) {
          setData({
            ...data,
            stageData: { currentStage: data.stageData.currentStage + 1, page: 0 },
            forceReload: false,
          });
        } else if (stage && stage <= 2) {
          setData({
            ...data,
            stageData: { currentStage: data.stageData.currentStage + 1, page: 0 },
            forceReload: false,
          });
        } else if (data.hotels && data.hotels.length === 0) {
          setData({
            hotels: [],
            stageData: { currentStage: 1, page: 1 },
            previousInfo: null,
            offset: 0,
            page: 0,
            forceReload: false,
          });
        }
      } else if (
        ((selectedLocation &&
          (selectedLocation.type === LOCATION_TYPE.STATE ||
            selectedLocation.type === LOCATION_TYPE.COUNTRY)) ||
          searchWithoutQuery) &&
        data.hotels.length > 0
      ) {
        setData({
          ...data,
          offset: data.offset + RESULTS_PER_PAGE,
          page: data.page + 1,
          forceReload: false,
        });
      }
    },
    [data, searchWithoutQuery, selectedLocation, setData],
  );

  const updateCityHotelData = useCallback(
    (hotelData: HotelApiResponse) => {
      let pageHotels: HotelApi[] = [];
      let extraHotels: HotelApi[] = [];
      let stage = 1;
      let total = 0;
      const existingIds = data.hotels.map((h: any) => h.id);
      hotelData.forEach((hotelItem) => {
        if (
          hotelItem.stage !== undefined &&
          hotelItem.stage >= data.stageData.currentStage &&
          pageHotels.length < RESULTS_PER_PAGE
        ) {
          const existingCount = pageHotels.length;
          const validHotels = hotelItem.hotels.filter(
            (item) => existingIds.indexOf(item.id) === -1,
          );
          if (hotelItem.stage === 3) {
            validHotels.forEach((hotel) => {
              // eslint-disable-next-line no-param-reassign
              hotel.isNearBy = true;
            });
          }
          pageHotels = [...pageHotels, ...validHotels.slice(0, RESULTS_PER_PAGE - existingCount)];
          stage = hotelItem.stage as number;
          total = hotelItem.total;
          extraHotels = validHotels.slice(RESULTS_PER_PAGE - existingCount);
        }
      });

      setNumHotels(hotelData.reduce((accu, curr) => accu + curr.total, 0));
      if (pageHotels.length > 0) {
        setData((prevData: any) => ({
          ...prevData,
          hotels: [...prevData.hotels, ...pageHotels],
          previousInfo: { stage, total, extraHotels },
          forceReload: false,
        }));
      } else if ((stage === 3 && data.stageData.currentStage === 3 && total > 0) || total === 0) {
        getMoreResults(stage, total, extraHotels);
      }
      setIsResetEnabled(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const updateStateOrCountryHotelData = useCallback(
    (hotelData: any) => {
      const existingIds = data.hotels.map((h: any) => h.id);
      const validHotels = hotelData.hotels.filter(
        (item: any) => existingIds.indexOf(item.id) === -1,
      );
      if (validHotels.length > 0) {
        setData((prevData: any) => ({
          ...prevData,
          hotels: [...prevData.hotels, ...validHotels],
          forceReload: false,
        }));
      } else if (data.hotels && data.hotels.length === 0) {
        setData({
          hotels: [],
          stageData: { currentStage: 1, page: 0 },
          previousInfo: null,
          offset: 0,
          page: 0,
          forceReload: false,
        });
      }
      setIsResetEnabled(true);
      setNumHotels(hotelData.total);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const getHotelData = useCallback(
    async (stage: number, page: number, filterData?: string[][]) => {
      let payloadData: any = {};
      if (selectedLocation) {
        // city search
        if (
          selectedLocation.type === LOCATION_TYPE.CITY ||
          selectedLocation.type === LOCATION_TYPE.CITY_ALIAS ||
          selectedLocation.type === LOCATION_TYPE.ALIAS
        ) {
          const isHighlightedHotel =
            selectedLocation.detailed_type === LOCATION_TYPE.CITY_HIGHLIGHTED;
          setIsLoading(true);
          const cityId = isHighlightedHotel ? selectedLocation.parent_id : selectedLocation.id;
          let query = `city_id:${cityId}&stage=${stage}&page=${page}&max_hits_per_page=${RESULTS_PER_PAGE}`;
          if (searchDate && !noDateSearch) {
            const formattedDate = getDateWithDashes(pastEscapedSearchDate(searchDate, dispatch));
            query = `${query}&date=${formattedDate}`;
          }
          if (onlyShowAvailableHotels) {
            query = `${query}&only_available=true`;
          }
          if (filterParams && filterParams.length > 0) {
            payloadData = { category_filter: filterParams };
          } else if (filterData) {
            payloadData = { category_filter: filterData };
          }

          // -----------------------------------------------------------
          // Use-Case Merchandising Filters ----------------------------
          // -----------------------------------------------------------
          // Search frontend implementation is pretty coupled right now,
          // but refactoring is out of scope for the use-case project.
          //
          // To support use-case, additional parameters has been added to the
          // query string and is used to inject additional algolia filters.
          // - Support filtering by product_type_ids
          // - Support filtering by hotel creation date
          // -----------------------------------------------------------
          if (productTypeIds) {
            payloadData.category_filter = payloadData.category_filter || [];
            const existing = payloadData.category_filter.findIndex(
              (filter: any) =>
                filter instanceof Array &&
                filter.find((elem) => elem.indexOf('product_type_id:') > -1),
            );
            if (existing >= 0) {
              payloadData.category_filter[existing] = productTypeIds
                .split(',')
                .map((id) => `product_type_id:${id}`);
            } else {
              payloadData.category_filter.push(
                productTypeIds.split(',').map((id) => `product_type_id:${id}`),
              );
            }
          }

          if (newAndNotable) {
            payloadData.category_filter = payloadData.category_filter || [];
            const dates = (() => {
              const today = new Date();
              const pad = (n: number) => (n < 10 ? `0${n}` : n.toString());
              return Array(...Array(90))
                .map((_, index) => {
                  if (index === 0) return today;
                  return new Date(new Date().setDate(today.getDate() - index));
                })
                .map(
                  (d) =>
                    `created_at:'${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}'`,
                );
            })();

            // Notable hotel must contain one of these product types:
            // Day Pass, Family Pass , Cabana, Daybed, Pool Chair, Spa Pass,
            // Spa Treatment, Massage, Facial, Nails, Couples Spa
            const pids = [1, 2, 3, 4, 7, 13, 50, 51, 52, 53, 55].map((i) => `product_type_id:${i}`);

            // find hotels with no ratings (new) or hotels with >= 4.5 (notable)
            const reviewScore = ['rating>=4.5', 'rating=0.0'];
            payloadData.category_filter.push(['closed_for_season:false']);
            payloadData.category_filter.push(dates);
            payloadData.category_filter.push(pids);
            payloadData.category_filter.push(reviewScore);
          }

          const hotelData = await getHotelsV5(query, payloadData);

          dispatch({
            type: actionTypes.UPDATE_SEARCH_RESULTS,
            payload: hotelData,
          });

          let selectedLocationName = encodeURIComponent(selectedLocation.name);
          if (isHighlightedHotel && stage === 1 && page === 0) {
            let hotelQuery = `name:"${selectedLocationName}"`;
            if (searchDate && !noDateSearch) {
              const formattedDate = getDateWithDashes(pastEscapedSearchDate(searchDate, dispatch));
              hotelQuery = `${hotelQuery}&date=${formattedDate}`;
            }
            if (onlyShowAvailableHotels) {
              hotelQuery = `${hotelQuery}&only_available=true`;
            }
            const singleHotelData = await getHotelsV5(hotelQuery, payloadData);
            const highlightedHotel =
              (singleHotelData as HotelApiResponseItem).hotels &&
              (singleHotelData as HotelApiResponseItem).hotels.length > 0
                ? (singleHotelData as HotelApiResponseItem).hotels[0]
                : null;
            if (highlightedHotel) {
              selectedLocationName = highlightedHotel?.name;
              highlightedHotel.isHighlighted = true;
              if (hotelData instanceof Array) {
                hotelData[0].hotels.unshift(highlightedHotel);
              }
            }
          }

          // Event
          search(
            selectedLocationName,
            'queryID' in hotelData ? hotelData.queryID : hotelData[0].queryID,
            searchDate,
          );

          updateCityHotelData(hotelData instanceof Array ? hotelData : []);
          setIsLoading(false);
          return;
        }

        // state search
        if (selectedLocation.type === LOCATION_TYPE.STATE) {
          const paramsString = selectedLocation.url.slice(selectedLocation.url.indexOf('?'));
          const params = new URLSearchParams(paramsString);
          const stateCode = params.get(LOCATION_TYPE.STATE) || '';
          setIsLoading(true);
          let query = `state_code:${stateCode}&page=${data.page}&max_hits_per_page=${RESULTS_PER_PAGE}`;
          if (searchDate && !noDateSearch) {
            const formattedDate = getDateWithDashes(pastEscapedSearchDate(searchDate, dispatch));
            query = `${query}&date=${formattedDate}`;
          }
          if (onlyShowAvailableHotels) {
            query = `${query}&only_available=true`;
          }
          if (filterParams && filterParams.length > 0) {
            payloadData = { category_filter: filterParams };
          } else if (filterData) {
            payloadData = { category_filter: filterData };
          }

          // -----------------------------------------------------------
          // Use-Case Merchandising Filters ----------------------------
          // -----------------------------------------------------------
          // Search frontend implementation is pretty coupled right now,
          // but refactoring is out of scope for the use-case project.
          //
          // To support use-case, additional parameters has been added to the
          // query string and is used to inject additional algolia filters.
          // - Support filtering by product_type_ids
          // - Support filtering by hotel creation date
          // -----------------------------------------------------------
          if (productTypeIds) {
            payloadData.category_filter = payloadData.category_filter || [];
            const existing = payloadData.category_filter.findIndex(
              (filter: any) =>
                filter instanceof Array &&
                filter.find((elem) => elem.indexOf('product_type_id:') > -1),
            );
            if (existing >= 0) {
              payloadData.category_filter[existing] = productTypeIds
                .split(',')
                .map((id) => `product_type_id:${id}`);
            } else {
              payloadData.category_filter.push(
                productTypeIds.split(',').map((id) => `product_type_id:${id}`),
              );
            }
          }

          if (newAndNotable) {
            payloadData.category_filter = payloadData.category_filter || [];
            const dates = (() => {
              const today = new Date();
              const pad = (n: number) => (n < 10 ? `0${n}` : n.toString());
              return Array(...Array(90))
                .map((_, index) => {
                  if (index === 0) return today;
                  return new Date(new Date().setDate(today.getDate() - index));
                })
                .map(
                  (d) =>
                    `created_at:'${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}'`,
                );
            })();

            // Notable hotel must contain one of these product types:
            // Day Pass, Family Pass , Cabana, Daybed, Pool Chair, Spa Pass,
            // Spa Treatment, Massage, Facial, Nails, Couples Spa
            const pids = [1, 2, 3, 4, 7, 13, 50, 51, 52, 53, 55].map((i) => `product_type_id:${i}`);

            // find hotels with no ratings (new) or hotels with >= 4.5 (notable)
            const reviewScore = ['rating>=4.5', 'rating=0.0'];
            payloadData.category_filter.push(['closed_for_season:false']);
            payloadData.category_filter.push(dates);
            payloadData.category_filter.push(pids);
            payloadData.category_filter.push(reviewScore);
          }
          const hotelData = await getHotelsV5(query, payloadData);

          dispatch({
            type: actionTypes.UPDATE_SEARCH_RESULTS,
            payload: hotelData,
          });

          // Event
          search(
            selectedLocation.name,
            'queryID' in hotelData ? hotelData.queryID : hotelData[0].queryID,
            searchDate,
          );

          updateStateOrCountryHotelData(hotelData);
          setIsLoading(false);
          return;
        }
        // country search
        if (selectedLocation.type === LOCATION_TYPE.COUNTRY) {
          const countryName = encodeURIComponent(selectedLocation.name);
          setIsLoading(true);
          let query = `country_name:"${countryName}"&page=${data.page}&max_hits_per_page=${RESULTS_PER_PAGE}`;
          if (searchDate && !noDateSearch) {
            const formattedDate = getDateWithDashes(pastEscapedSearchDate(searchDate, dispatch));
            query = `${query}&date=${formattedDate}`;
          }
          if (onlyShowAvailableHotels) {
            query = `${query}&only_available=true`;
          }
          if (filterParams && filterParams.length > 0) {
            payloadData = { category_filter: filterParams };
          } else if (filterData) {
            payloadData = { category_filter: filterData };
          }

          // -----------------------------------------------------------
          // Use-Case Merchandising Filters ----------------------------
          // -----------------------------------------------------------
          // Search frontend implementation is pretty coupled right now,
          // but refactoring is out of scope for the use-case project.
          //
          // To support use-case, additional parameters has been added to the
          // query string and is used to inject additional algolia filters.
          // - Support filtering by product_type_ids
          // - Support filtering by hotel creation date
          // -----------------------------------------------------------
          if (productTypeIds) {
            payloadData.category_filter = payloadData.category_filter || [];
            const existing = payloadData.category_filter.findIndex(
              (filter: any) =>
                filter instanceof Array &&
                filter.find((elem) => elem.indexOf('product_type_id:') > -1),
            );
            if (existing >= 0) {
              payloadData.category_filter[existing] = productTypeIds
                .split(',')
                .map((id) => `product_type_id:${id}`);
            } else {
              payloadData.category_filter.push(
                productTypeIds.split(',').map((id) => `product_type_id:${id}`),
              );
            }
          }

          if (newAndNotable) {
            payloadData.category_filter = payloadData.category_filter || [];
            const dates = (() => {
              const today = new Date();
              const pad = (n: number) => (n < 10 ? `0${n}` : n.toString());
              return Array(...Array(90))
                .map((_, index) => {
                  if (index === 0) return today;
                  return new Date(new Date().setDate(today.getDate() - index));
                })
                .map(
                  (d) =>
                    `created_at:'${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}'`,
                );
            })();

            // Notable hotel must contain one of these product types:
            // Day Pass, Family Pass , Cabana, Daybed, Pool Chair, Spa Pass,
            // Spa Treatment, Massage, Facial, Nails, Couples Spa
            const pids = [1, 2, 3, 4, 7, 13, 50, 51, 52, 53, 55].map((i) => `product_type_id:${i}`);

            // find hotels with no ratings (new) or hotels with >= 4.5 (notable)
            const reviewScore = ['rating>=4.5', 'rating=0.0'];
            payloadData.category_filter.push(['closed_for_season:false']);
            payloadData.category_filter.push(dates);
            payloadData.category_filter.push(pids);
            payloadData.category_filter.push(reviewScore);
          }
          const hotelData = await getHotelsV5(query, payloadData);

          dispatch({
            type: actionTypes.UPDATE_SEARCH_RESULTS,
            payload: hotelData,
          });

          // Event
          search(
            selectedLocation.name,
            'queryID' in hotelData ? hotelData.queryID : hotelData[0].queryID,
            searchDate,
          );

          updateStateOrCountryHotelData(hotelData);
          setIsLoading(false);
        }
        return;
      }

      // all hotels page
      if (searchWithoutQuery) {
        setIsLoading(true);
        let query = `&page=${data.page}&max_hits_per_page=${RESULTS_PER_PAGE}`;
        if (searchDate && !noDateSearch) {
          const formattedDate = getDateWithDashes(pastEscapedSearchDate(searchDate, dispatch));
          query = `${query}&date=${formattedDate}`;
        }
        if (onlyShowAvailableHotels) {
          query = `${query}&only_available=true`;
        }
        if (filterParams && filterParams.length > 0) {
          payloadData = { category_filter: filterParams };
        } else if (filterData) {
          payloadData = { category_filter: filterData };
        }

        // -----------------------------------------------------------
        // Use-Case Merchandising Filters ----------------------------
        // -----------------------------------------------------------
        // Search frontend implementation is pretty coupled right now,
        // but refactoring is out of scope for the use-case project.
        //
        // To support use-case, additional parameters has been added to the
        // query string and is used to inject additional algolia filters.
        // - Support filtering by product_type_ids
        // - Support filtering by hotel creation date
        // -----------------------------------------------------------
        if (productTypeIds) {
          payloadData.category_filter = payloadData.category_filter || [];
          const existing = payloadData.category_filter.findIndex(
            (filter: any) =>
              filter instanceof Array &&
              filter.find((elem) => elem.indexOf('product_type_id:') > -1),
          );
          if (existing >= 0) {
            payloadData.category_filter[existing] = productTypeIds
              .split(',')
              .map((id) => `product_type_id:${id}`);
          } else {
            payloadData.category_filter.push(
              productTypeIds.split(',').map((id) => `product_type_id:${id}`),
            );
          }
        }

        if (newAndNotable) {
          payloadData.category_filter = payloadData.category_filter || [];
          const dates = (() => {
            const today = new Date();
            const pad = (n: number) => (n < 10 ? `0${n}` : n.toString());
            return Array(...Array(90))
              .map((_, index) => {
                if (index === 0) return today;
                return new Date(new Date().setDate(today.getDate() - index));
              })
              .map(
                (d) =>
                  `created_at:'${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}'`,
              );
          })();

          // Notable hotel must contain one of these product types:
          // Day Pass, Family Pass , Cabana, Daybed, Pool Chair, Spa Pass,
          // Spa Treatment, Massage, Facial, Nails, Couples Spa
          const pids = [1, 2, 3, 4, 7, 13, 50, 51, 52, 53, 55].map((i) => `product_type_id:${i}`);

          // find hotels with no ratings (new) or hotels with >= 4.5 (notable)
          const reviewScore = ['rating>=4.5', 'rating=0.0'];
          payloadData.category_filter.push(['closed_for_season:false']);
          payloadData.category_filter.push(dates);
          payloadData.category_filter.push(pids);
          payloadData.category_filter.push(reviewScore);
        }
        const hotelData = await getHotelsV5(query, payloadData);

        dispatch({
          type: actionTypes.UPDATE_SEARCH_RESULTS,
          payload: hotelData,
        });

        updateStateOrCountryHotelData(hotelData);
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLocation, data, searchDate, filterParams, searchWithoutQuery],
  );

  useEffect(() => {
    if (!data.forceReload) {
      getHotelData(data.stageData.currentStage, data.stageData.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.stageData.currentStage, data.stageData.page, data.offset, data.page]);

  useEffect(() => {
    if (isResetEnabled) resetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDate, onlyShowAvailableHotels, filterParams]);

  useEffect(() => {
    if (!router.isReady) return;
    if (
      srpData &&
      srpData.hotels &&
      srpData.hotels.length > 0 &&
      windowUrl === window.location.href
    ) {
      setData({
        hotels: srpData.hotels,
        stageData: srpData.stageData,
        previousInfo: srpData.previousInfo,
        offset: 0,
        page: 0,
        forceReload: false,
      });
      setIsResetEnabled(true);
      setTimeout(() => {
        dispatch({
          type: actionTypes.UPDATE_WINDOW_URL,
          payload: '',
        });
      }, 2000);
      return;
    }
    if ((data.forceReload && selectedLocation) || searchWithoutQuery) {
      getHotelData(1, 0);
      window?.scrollTo(0, 0);
      if (selectedLocation) setCurrentLocation(selectedLocation);
    } else if (
      selectedLocation &&
      (!currentLocation || (currentLocation && currentLocation.id !== selectedLocation.id))
    ) {
      window?.scrollTo(0, 0);
      clearFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.forceReload, selectedLocation, router.isReady, searchWithoutQuery]);

  useEffect(() => {
    const { filters } = router.query;
    if (!router.isReady) return;
    if (!filters) return;
    if (data.forceReload && selectedLocation && filterParams) {
      getHotelData(1, 0);
      window?.scrollTo(0, 0);
      setCurrentLocation(selectedLocation);
    } else if (
      selectedLocation &&
      (!currentLocation || (currentLocation && currentLocation.id !== selectedLocation.id))
    ) {
      window?.scrollTo(0, 0);
      resetData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.forceReload, selectedLocation, router.isReady, filterParams]);

  useEffect(() => {
    if (data.hotels && data.hotels.length > 0) {
      dispatch({
        type: actionTypes.UPDATE_SRP_DATA,
        payload: data,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.hotels]);

  useEffect(() => {
    if (setTitleHotelName) {
      if (selectedLocation?.name) {
        setTitleHotelName(selectedLocation.name);
      } else if (data.hotels && data.hotels.length > 0) {
        setTitleHotelName(data.hotels[0].city_name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.hotels, selectedLocation]);

  useEffect(() => {
    const completeHandler = (path: string) => {
      if (
        srpScrollPosition &&
        (path.indexOf('hotel-day-passes') > -1 || path.indexOf('results') > -1)
      ) {
        window?.scrollTo(0, srpScrollPosition);
        dispatch({
          type: actionTypes.UPDATE_SRP_SCROLL_POSITION,
          payload: null,
        });
      }
    };
    if (srpScrollPosition && windowUrl === window.location.href && router) {
      router.events.on('routeChangeComplete', completeHandler);
    }
    return () => {
      router.events.off('routeChangeComplete', completeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.hotels, router]);

  useEffect(() => {
    if (srpScrollPosition && windowUrl === window.location.href) {
      return;
    }
    window?.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, searchDate, onlyShowAvailableHotels]);

  useEffect(() => {
    // GA4 event
    setTimeout(() => {
      const resetFiltersButton = document.getElementById('reset-filters-button');
      if (resetFiltersButton) {
        let filtersApplied = '';
        const storedFilters = getItem(sessionStorageKeys.SRP_FILTERS);
        const showOnlyAvailableFilter = getItem(sessionStorageKeys.SHOW_ONLY_AVAILABLE_FILTER);
        if (storedFilters) {
          const parsedFilters = JSON.parse(storedFilters);
          for (const [key, value] of Object.entries(parsedFilters)) {
            if (key && value && key !== 'url') {
              for (const [nestedKey, nestedValue] of Object.entries(value as Object)) {
                if (nestedKey && nestedValue && nestedKey !== 'any') {
                  filtersApplied += `${nestedKey}, `;
                }
              }
            }
          }
        }
        if (showOnlyAvailableFilter === 'true') {
          filtersApplied += 'only_available';
        }
        filtersApplied = filtersApplied.replace(/,\s*$/, '');
        if (appliedFilters !== filtersApplied) {
          filterZeroResultsViewed(selectedLocation, searchDate, filtersApplied);
          appliedFilters = filtersApplied;
        }
      }
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchDate, selectedLocation]);

  const loadMoreClickHandler = useCallback(() => {
    if (loadMoreClicked) return;
    setLoadMoreClicked(true);

    // ResultsList in this experiment caps the initial results to 30 hotels.
    //
    // If the initial fetch contains more than 30 hotels, we will
    // skip fetching more hotels and simply let ResultsList render
    // the remaining hotels from the initial fetch.
    if ((data?.hotels?.length ?? 0) > 30) {
      return;
    }
    getMoreResults();
  }, [loadMoreClicked, setLoadMoreClicked, data, getMoreResults]);

  return (
    <>
      {isLoading && <ResortPassAnimation />}
      <InfiniteScroll
        dataLength={data.hotels.length}
        next={getMoreResults}
        hasMore={!!getMoreResults && loadMoreClicked}
        loader={null}
      >
        <ResultList
          data={data}
          filterParams={filterParams}
          selectedLocation={selectedLocation}
          isFirstQuery={!loadMoreClicked}
          clearFilters={clearFilters}
          isLoading={isLoading}
        />
      </InfiniteScroll>

      {!!getMoreResults && !isLoading && !loadMoreClicked && numHotels > RESULTS_PER_PAGE && (
        <div className="flex justify-center mx-6 mt-6 d:max-w-1024 d:min-w-850 d:mx-0">
          <ButtonPrimary onClick={loadMoreClickHandler}>Show more hotels</ButtonPrimary>
        </div>
      )}
    </>
  );
}
